if (typeof debounce === 'undefined') {
  var debounce = function(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}
/**
 *  Filters and sort on the collection and search grids.
 *
 *  The full page carries the filters once, in the desktop sidebar. The phone sheet's rows and the bar's filter
 *  panels are left empty (data-facets-pending) and are filled the first time one is opened, from this section's Section
 *  Rendering API response, which renders them (FacetFiltersForm.loadDeferred). Every filter or sort change
 *  requests the section again: only the latest request is rendered, a failed one keeps the current results.
 */

/**
 *  @class
 *  @function FacetsToggle
 *  A Filter button: opens the filter sheet (a modal dialog) and reports its state in aria-expanded.
 */
if (!customElements.get('facets-toggle')) {
  class FacetsToggle extends HTMLElement {
    constructor() {
      super();
      this.syncExpanded = this.syncExpanded.bind(this);
      this.addEventListener('click', this.onClick.bind(this));
    }
    connectedCallback() {
      this.drawer = document.getElementById('Facet-Drawer');
      document.addEventListener('panel:open', this.syncExpanded);
      document.addEventListener('panel:closed', this.syncExpanded);
      this.syncExpanded();
    }
    disconnectedCallback() {
      document.removeEventListener('panel:open', this.syncExpanded);
      document.removeEventListener('panel:closed', this.syncExpanded);
    }
    onClick() {
      if (!this.drawer) return;
      // With a bar panel open, Filter first closes that panel.
      const quick = this.closest('facets-quick');
      if (quick && quick.openId) {
        quick.close(false);
        return;
      }
      if (OverlayManager.isOpen(this.drawer)) {
        OverlayManager.close(this.drawer);
      } else {
        OverlayManager.open(this.drawer);
        customElements.get('facet-filters-form')?.loadDeferred();
      }
    }
    syncExpanded() {
      const button = this.querySelector('button[aria-controls]');
      if (button && this.drawer) button.setAttribute('aria-expanded', OverlayManager.isOpen(this.drawer) ? 'true' : 'false');
    }
  }
  customElements.define('facets-toggle', FacetsToggle);
}

/**
 *  @class
 *  @function FacetsSheet
 *  The filter sheet's wrapper: Escape closes the sheet wherever focus is inside it.
 */
if (!customElements.get('facets-sheet')) {
  class FacetsSheet extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('keydown', (event) => {
        const drawer = this.querySelector('#Facet-Drawer');
        if (event.key !== 'Escape' || !drawer || !OverlayManager.isOpen(drawer)) return;
        event.stopPropagation();
        OverlayManager.close(drawer);
      });
    }
  }
  customElements.define('facets-sheet', FacetsSheet);
}

/**
 *  @class
 *  @function FacetsQuick
 *  The filter bar's panels: a button opens its panel over the grid, directly under
 *  the bar, with a scrim over the rest of the page, which stays still meanwhile. One panel at a time;
 *  the same button, Escape, the scrim, "See results", tapping a sort or moving focus out of the bar closes it.
 *  Arrow keys step through the sorts without closing. Closing returns focus to the panel's button only when
 *  focus was in the bar, so a tap (which leaves focus on the page in Safari) gets no scripted focus ring.
 */
if (!customElements.get('facets-quick')) {
  class FacetsQuick extends HTMLElement {
    constructor() {
      super();
      this.openId = null;
      this.resultsChanged = false;
      this.onResize = this.place.bind(this);
      this.onOverlayOpen = () => this.close(false);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('pointerdown', () => (this.byPointer = true));
      this.addEventListener('keydown', () => (this.byPointer = false));
    }
    hasFocus() {
      return this.contains(document.activeElement);
    }
    disconnectedCallback() {
      // The bar is re-rendered with the grid; the new bar re-opens the panel (renderProductGridContainer).
      if (this.openId) this.release();
    }
    onClick(event) {
      const button = event.target.closest('button[aria-controls]');
      if (button && !button.closest('facets-toggle')) {
        const id = button.getAttribute('aria-controls');
        if (this.openId === id) this.close(this.hasFocus());
        // Keyboard opening moves focus into the panel; a tap leaves it, so no scripted focus ring reads as a choice.
        else this.open(id, !this.byPointer);
        return;
      }
      if (event.target.closest('[data-quick-close]')) this.close(this.hasFocus());
    }
    // Escape works wherever focus is while a panel is open.
    onDocumentKeydown(event) {
      if (event.key !== 'Escape' || !this.openId) return;
      event.stopPropagation();
      this.close(this.contains(document.activeElement) || document.activeElement === document.body);
    }
    // A disclosure: it closes when keyboard focus leaves the bar (its buttons and panels).
    onFocusOut(event) {
      if (!this.openId || !event.relatedTarget || this.contains(event.relatedTarget)) return;
      this.close(false);
    }
    onChange(event) {
      if (event.target.name === 'sort_by' && this.byPointer) this.close(this.hasFocus());
    }
    open(id, moveFocus = true) {
      const panel = document.getElementById(id);
      if (!panel) return;
      if (!this.openId) this.hold();
      this.openId = id;
      this.place();
      this.sync();
      const focusFirst = () => {
        if (!moveFocus || this.openId !== id) return;
        const target = panel.querySelector('input:checked:not(:disabled)') || panel.querySelector('input:not(:disabled)');
        if (target) target.focus({ preventScroll: true });
      };
      const form = customElements.get('facet-filters-form');
      if (form && panel.querySelector('[data-facets-pending]')) {
        form.loadDeferred().then(focusFirst);
      } else {
        focusFirst();
      }
    }
    close(restoreFocus) {
      const id = this.openId;
      if (!id) return;
      this.openId = null;
      this.sync();
      this.release();
      if (restoreFocus) this.querySelector(`button[aria-controls="${id}"]`)?.focus({ preventScroll: true });
      // The results changed while the panel held the page still: show the first of them now.
      if (this.resultsChanged) {
        this.resultsChanged = false;
        customElements.get('facet-filters-form')?.scrollToResults();
      }
    }
    // The page stays still while a panel is open, so the panel's top edge stays valid until it closes.
    hold() {
      document.body.classList.add('facets-quick-open');
      window.addEventListener('resize', this.onResize);
      document.addEventListener('panel:open', this.onOverlayOpen);
      document.addEventListener('keydown', this.onDocumentKeydown);
      this.addEventListener('focusout', this.onFocusOut);
    }
    release() {
      document.body.classList.remove('facets-quick-open');
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('panel:open', this.onOverlayOpen);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      this.removeEventListener('focusout', this.onFocusOut);
    }
    // The panel spans the screen under the button row; its content lines up with the bar's edges.
    place() {
      const row = this.querySelector('.facets-quick__row');
      const bar = this.closest('form');
      if (!row || !bar) return;
      const edges = bar.getBoundingClientRect();
      this.style.setProperty('--quick-top', `${Math.round(row.getBoundingClientRect().bottom)}px`);
      this.style.setProperty('--quick-inset-start', `${Math.round(edges.left)}px`);
      this.style.setProperty('--quick-inset-end', `${Math.round(document.documentElement.clientWidth - edges.right)}px`);
    }
    sync() {
      this.querySelectorAll('.facets-quick__row button[aria-controls]').forEach((button) => {
        if (button.closest('facets-toggle')) return;
        button.setAttribute('aria-expanded', button.getAttribute('aria-controls') === this.openId ? 'true' : 'false');
      });
      this.querySelectorAll('.facets-quick__panel').forEach((panel) => {
        panel.hidden = panel.id !== this.openId;
      });
      const scrim = this.querySelector('.facets-quick__scrim');
      if (scrim) scrim.hidden = !this.openId;
    }
  }
  customElements.define('facets-quick', FacetsQuick);
}

if (!customElements.get('facet-filters-form')) {
  class FacetFiltersForm extends HTMLElement {
    constructor() {
      super();
      this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

      this.debouncedOnSubmit = debounce((event) => {
        this.onSubmitHandler(event);
      }, 500);

      // The controls the shopper changed since the last request. A newer choice supersedes any request still loading
      // at once, so an older response never lands over it. A price commit names both of its fields.
      this.touched = new Set();
      this.querySelector('form').addEventListener('input', (event) => {
        const price = event.target.closest && event.target.closest('price-slider');
        (price ? price.querySelectorAll('input') : [event.target]).forEach((el) => {
          if (el.name) this.touched.add(el);
        });
        FacetFiltersForm.cancelPending();
        this.debouncedOnSubmit(event);
      });
    }

    static cancelPending() {
      if (!FacetFiltersForm.controller) return;
      FacetFiltersForm.controller.abort();
      FacetFiltersForm.controller = null;
    }

    static setListeners() {
      const onHistoryChange = (event) => {
        const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
        if (searchParams === FacetFiltersForm.searchParamsPrev) return;
        FacetFiltersForm.renderPage(searchParams, null, false);
      };
      window.addEventListener('popstate', onHistoryChange);
      // A panel that failed to load offers Try again. If it had focus (keyboard; a tap in Safari does not focus it),
      // focus goes to the first choice that replaces it.
      document.addEventListener('click', (event) => {
        const retry = event.target.closest('[data-facets-retry]');
        if (!retry) return;
        const slot = retry.closest('[data-facets-deferred]');
        const hadFocus = document.activeElement === retry;
        FacetFiltersForm.loadDeferred().then(() => {
          if (!hadFocus) return;
          // Filled: the first choice. Failed again: back to Try again, shown once more.
          const next = slot.hasAttribute('data-facets-pending') ? slot.querySelector('[data-facets-retry]') : slot.querySelector('input:not(:disabled)');
          next?.focus({ preventScroll: true });
        });
      });
    }

    static sectionUrl(searchParams) {
      const grid = document.getElementById('product-grid');
      if (!grid) return null;
      return `${window.location.pathname}?section_id=${grid.dataset.id}&${searchParams}`;
    }

    // One section response per state, cached; a non-OK or malformed response is an error and is not cached.
    static fetchSection(url, signal) {
      const cached = FacetFiltersForm.filterData.find((entry) => entry.url === url);
      if (cached) return Promise.resolve(cached.html);
      return fetch(url, { signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          if (html.indexOf('id="ProductGridContainer"') === -1) throw new Error('Section response has no product grid');
          FacetFiltersForm.filterData.push({ html, url });
          return html;
        });
    }

    static setLoading(loading) {
      document.querySelector('#ProductGridContainer .collection')?.classList.toggle('loading', loading);
      for (const item of document.getElementsByClassName('thb-filter-count')) {
        item.classList.toggle('loading', loading);
      }
    }

    // The grid's one status region (role=status, outside the re-rendered container).
    static announce(message) {
      const status = document.querySelector('[data-results-status]');
      if (!status || !message) return;
      status.textContent = '';
      requestAnimationFrame(() => {
        status.textContent = message;
      });
    }

    static renderPage(searchParams, event, updateURLHash = true) {
      const url = FacetFiltersForm.sectionUrl(searchParams);
      if (!url) return;
      FacetFiltersForm.searchParamsPrev = searchParams;
      FacetFiltersForm.setLoading(true);
      FacetFiltersForm.showError(false);

      // Only the latest state is rendered: an earlier request still in flight is cancelled.
      if (FacetFiltersForm.controller) FacetFiltersForm.controller.abort();
      const controller = new AbortController();
      FacetFiltersForm.controller = controller;

      FacetFiltersForm.fetchSection(url, controller.signal)
        .then((html) => {
          if (controller.signal.aborted) return;
          FacetFiltersForm.controller = null;
          const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
          const focusTo = FacetFiltersForm.focusTargets();
          withViewTransition(() => {
            FacetFiltersForm.renderFilters(parsedHTML, event);
            FacetFiltersForm.renderProductGridContainer(parsedHTML);
            FacetFiltersForm.renderProductCount(parsedHTML);
            // A sheet or quick panel not filled yet takes this state too, so an earlier request cannot fill it stale.
            FacetFiltersForm.fillDeferred(parsedHTML);
            FacetFiltersForm.restoreFocus(focusTo);
          });
          // The address bar follows the results it shows; a change that ends on the state already there adds no entry.
          if (updateURLHash && searchParams !== window.location.search.slice(1)) FacetFiltersForm.updateURLHash(searchParams);
          FacetFiltersForm.publishFacetUpdate(searchParams);
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          FacetFiltersForm.controller = null;
          // The current results stay; the shopper is told and can try again.
          FacetFiltersForm.setLoading(false);
          FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
          FacetFiltersForm.restoreControls();
          FacetFiltersForm.showError(true);
          FacetFiltersForm.announce(FacetFiltersForm.errorMessage());
          console.error(error);
        });
    }

    // The visible failure line; the status region announces the same text.
    static showError(show) {
      const el = document.querySelector('[data-results-error]');
      if (el) el.hidden = !show;
    }

    // Puts every filter and sort control back to the results still shown. Each control's default is that state:
    // rows and the bar are rendered from the response shown, and the row a change came from takes its new state as
    // its default. Nothing is re-rendered, so keyboard focus stays on the control the shopper used.
    static restoreControls() {
      const forms = '#FacetFiltersForm, #FacetFiltersFormMobile, #FacetFiltersForm-bar';
      document.querySelectorAll(`:is(${forms}) input:not([type="hidden"])`).forEach((input) => {
        if (input.type === 'checkbox' || input.type === 'radio') input.checked = input.defaultChecked;
        else input.value = input.defaultValue;
      });
      document.querySelectorAll(`:is(${forms}) select option`).forEach((option) => {
        option.selected = option.defaultSelected;
      });
      // An empty field means no price limit: its handle goes to the end of the range, as when the slider was built.
      document.querySelectorAll(`:is(${forms}) price-slider`).forEach((row) => {
        const slider = row.querySelector('.price_slider');
        const fields = row.querySelectorAll('.price_slider_amount input');
        if (!slider || !slider.noUiSlider || fields.length !== 2) return;
        slider.noUiSlider.set([fields[0].defaultValue || 0, fields[1].defaultValue || slider.dataset.max]);
      });
    }

    static errorMessage() {
      return document.querySelector('[data-results-status]')?.dataset.error || '';
    }

    // shopify:collection:update / shopify:search:update — filters or sort changed
    static publishFacetUpdate(searchParams) {
      const SE = window.Shopify && window.Shopify.StandardEvents;
      if (!SE) return;
      const params = new URLSearchParams(searchParams);

      if (theme.settings.collection_id) {
        publishStandardEvent('CollectionUpdateEvent', {
          collection: { id: theme.settings.collection_id },
          filters: SE.CollectionUpdateEvent.parseProductFilters(params),
          sortKey: SE.CollectionUpdateEvent.getSortKey(params)
        });
      } else if (window.location.pathname.indexOf(theme.routes.search_url) !== -1) {
        publishStandardEvent('SearchUpdateEvent', {
          searchTerm: params.get('q') || new URLSearchParams(window.location.search).get('q') || '',
          filters: SE.SearchUpdateEvent.parseProductFilters(params),
          sortKey: SE.SearchUpdateEvent.getSortKey(params)
        });
      }
    }

    // Fills the panels the page left empty (data-facets-pending: the sheet's rows, the bar's filter panels) from the
    // section response for the current state. Resolves once they are filled. They are busy only while the request
    // runs; a failure shows the slot's own message with a Try again button, announced. It asks for the state being
    // rendered (a filter change still loading has not reached the address bar yet), and an answer for a state that
    // has since changed is dropped: the change's own response fills the slots.
    static loadDeferred() {
      const pending = () => document.querySelectorAll('[data-facets-pending]');
      if (!pending().length) return Promise.resolve();
      if (FacetFiltersForm.deferredRequest) return FacetFiltersForm.deferredRequest;
      const searchParams = FacetFiltersForm.searchParamsPrev ?? window.location.search.slice(1);
      const url = FacetFiltersForm.sectionUrl(searchParams);
      if (!url) return Promise.resolve();
      // The spinner shows while busy (facets.css); the message only after a failure.
      const setState = (state) => pending().forEach((slot) => {
        slot.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
        slot.querySelector('.facets-deferred__error')?.toggleAttribute('hidden', state !== 'error');
      });
      setState('loading');
      let stale = false;
      FacetFiltersForm.deferredRequest = FacetFiltersForm.fetchSection(url)
        .then((html) => {
          if (FacetFiltersForm.searchParamsPrev !== searchParams) {
            stale = true;
            return;
          }
          FacetFiltersForm.fillDeferred(new DOMParser().parseFromString(html, 'text/html'));
        })
        .catch((error) => {
          if (FacetFiltersForm.searchParamsPrev !== searchParams) {
            stale = true;
            return;
          }
          setState('error');
          FacetFiltersForm.announce(document.querySelector('[data-facets-pending] .facets-deferred__message')?.textContent.trim());
          console.error(error);
        })
        .finally(() => {
          FacetFiltersForm.deferredRequest = null;
        })
        // A dropped answer: ask again for the current state, unless the change's response already filled the slots.
        .then(() => (stale ? FacetFiltersForm.loadDeferred() : undefined));
      return FacetFiltersForm.deferredRequest;
    }

    // Fills any panel still left empty from a section response for the state being shown.
    static fillDeferred(parsedHTML) {
      document.querySelectorAll('[data-facets-pending]').forEach((slot) => {
        const source = parsedHTML.querySelector(`[data-facets-deferred="${slot.dataset.facetsDeferred}"]`);
        if (!source || source.hasAttribute('data-facets-pending')) return;
        slot.innerHTML = source.innerHTML;
        slot.removeAttribute('data-facets-pending');
        slot.removeAttribute('aria-busy');
      });
    }

    // Where keyboard focus goes back after the results re-render, as selectors tried in order: the same control,
    // else its nearest stand-in (a panel's Clear becomes disabled, so its "See results"; a removed filter chip,
    // the next chip, then the bar's Filter button). Focus outside the re-rendered parts is left alone.
    static focusTargets() {
      const active = document.activeElement;
      const scope = active && active.closest('#ProductGridContainer, .facets-desktop-container, #Facet-Drawer');
      if (!scope) return [];
      const targets = active.id ? [`#${CSS.escape(active.id)}`] : [];
      const panel = active.closest('.facets-quick__panel');
      if (panel) targets.push(`#${CSS.escape(panel.id)} .facets-quick__done`);
      if (active.closest('.active-facets')) targets.push('#ProductGridContainer .active-facets a', '#FacetsQuick-filter-button', '#Facets-Toggle');
      return targets;
    }

    static restoreFocus(targets) {
      for (const selector of targets) {
        const el = document.querySelector(selector);
        if (el && !el.disabled && el.getClientRects().length) {
          if (el !== document.activeElement) el.focus({ preventScroll: true });
          return;
        }
      }
    }

    static renderProductGridContainer(parsedHTML) {
      const container = document.getElementById('ProductGridContainer');
      const source = parsedHTML.getElementById('ProductGridContainer');
      if (!container || !source) return;
      // The filter bar is part of this container: keep its open panel and the focused control across the swap.
      const openPanel = container.querySelector('facets-quick')?.openId;
      container.innerHTML = source.innerHTML;
      if (openPanel) {
        const quick = container.querySelector('facets-quick');
        if (quick) {
          quick.open(openPanel, false);
          // With a bar panel open the page is held still under it; the shopper sees the first results on closing.
          quick.resultsChanged = true;
        }
      }
      if (openPanel) return;
      FacetFiltersForm.scrollToResults();
    }

    // Back to the first row of results. A pinned (sticky) bar reports its pinned position, so the target is
    // measured from the grid, which never sticks, less the header and the pinned bar above it.
    static scrollToResults() {
      const container = document.getElementById('ProductGridContainer');
      const grid = document.getElementById('product-grid');
      if (!container || !grid) return;
      const header = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
      const bar = container.querySelector('.facets--bar');
      let top;
      if (bar && getComputedStyle(bar).position === 'sticky') {
        top = grid.getBoundingClientRect().top + window.scrollY - header - bar.offsetHeight;
      } else {
        // A bar that scrolls with the page stays in view above the results, as before.
        top = (bar || grid).getBoundingClientRect().top + window.scrollY - header - 30;
      }
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(0, top), left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    static renderProductCount(parsedHTML) {
      const countHtml = parsedHTML.getElementById('ProductCount');
      for (const item of document.getElementsByClassName('thb-filter-count')) {
        if (countHtml) item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
      // Screen readers hear how many products the new filters or sort show.
      FacetFiltersForm.announce(countHtml?.querySelector('.facets__label')?.textContent.trim());
    }

    static renderFilters(parsedHTML, event) {
      const facetDetailsElements =
        parsedHTML.querySelectorAll('#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row, #FacetFiltersForm-bar collapsible-row');
      const jsFilter = event && event.target ? event.target.closest('collapsible-row') : null;
      const matchesIndex = (element) => {
        return jsFilter ? element.dataset.index === jsFilter.dataset.index && element.closest('form').id === jsFilter.closest('form').id : false;
      };
      const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));

      // Rows are numbered per form, so the sidebar and the sheet share numbers: match within the same form.
      // A sheet that has not been opened yet has no rows; it is filled from the current state when it opens.
      facetsToRender.forEach((element) => {
        const form = element.closest('form');
        const target = document.querySelector(`#${form.id} collapsible-row[data-index="${element.dataset.index}"]`);
        if (!target) return;
        // Keep a group the shopper opened or closed as it was; the server renders the default state.
        const open = target.querySelector('details')?.open;
        target.innerHTML = element.innerHTML;
        const details = target.querySelector('details');
        if (details && open !== undefined) details.open = open;
      });

      // The row the change came from is kept as it is: what it shows is now its rendered state.
      jsFilter?.querySelectorAll('input').forEach((input) => {
        input.defaultChecked = input.checked;
        input.defaultValue = input.value;
      });

      FacetFiltersForm.renderActiveFacets(parsedHTML);
      FacetFiltersForm.renderAdditionalElements(parsedHTML);
    }

    static renderActiveFacets(html) {
      const activeFacetElementSelectors = ['.active-facets'];

      activeFacetElementSelectors.forEach((selector) => {
        const activeFacetsElement = html.querySelector(selector);
        const target = document.querySelector(selector);
        if (!activeFacetsElement || !target) return;
        target.innerHTML = activeFacetsElement.innerHTML;
      });
    }

    static renderAdditionalElements(html) {
      // The sheet's footer and the sidebar's "Clear all" sit outside the filter rows, so they are swapped here.
      const elementSelectors = ['#Facet-Drawer .side-panel-footer', '.facets-desktop__clear'];

      elementSelectors.forEach((selector) => {
        const element = document.querySelector(selector);
        if (!element || !html.querySelector(selector)) return;
        const hadFocus = element.contains(document.activeElement);
        element.innerHTML = html.querySelector(selector).innerHTML;
        // Clear may now be disabled or gone; focus moves to "See results" in the sheet, the first filter in the sidebar.
        if (hadFocus) (element.querySelector('.mobile-filters-apply') || element.closest('form')?.querySelector('summary'))?.focus();
      });

    }

    static updateURLHash(searchParams) {
      history.pushState({
        searchParams
      }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }

    onSubmitHandler(event) {
      event.preventDefault();
      // This component's own form: the control that fired may since have been replaced by a re-render.
      const form = this.querySelector('form');
      // A price being typed and not yet committed holds the change: its commit (Enter, or leaving the field) submits.
      const typing = document.activeElement && document.activeElement.closest && document.activeElement.closest('price-slider');
      if (typing && form.contains(typing) && typing.hasUncommittedPrice()) {
        typing.held = true;
        return;
      }
      // The state being shown or still loading, with the controls the shopper changed in this form since then applied
      // over it, as they are now: a choice undone while its own request loads is taken out again, and another form's
      // change still loading is kept. A change starts at page 1.
      const searchParams = new URLSearchParams(FacetFiltersForm.searchParamsPrev);
      searchParams.delete('page');
      const touched = Array.from(this.touched).filter((el) => form.contains(el) && !el.disabled);
      this.touched.clear();
      touched.forEach((el) => {
        // A price is what the slider holds: it moves only when a price is committed, not while one is being typed.
        const price = el.closest('price-slider')?.querySelector('.price_slider')?.noUiSlider;
        if (price) {
          const value = price.get()[el.classList.contains('field__input_min') ? 0 : 1];
          if (value !== '') searchParams.set(el.name, value);
          else searchParams.delete(el.name);
          return;
        }
        if (el.type === 'radio') {
          if (el.checked) searchParams.set(el.name, el.value);
        } else if (el.type === 'checkbox') {
          const others = searchParams.getAll(el.name).filter((value) => value !== el.value);
          searchParams.delete(el.name);
          others.forEach((value) => searchParams.append(el.name, value));
          if (el.checked) searchParams.append(el.name, el.value);
        } else if (el.value) {
          searchParams.set(el.name, el.value);
        } else {
          searchParams.delete(el.name);
        }
      });
      if (searchParams.get('filter.v.price.gte') === "0.00") {
        searchParams.delete('filter.v.price.gte');
      }
      const priceSlider = form.querySelector('.price_slider') || document.querySelector('.price_slider');
      if (priceSlider) {
        if (searchParams.get('filter.v.price.lte') === parseFloat(priceSlider.dataset.max).toFixed(2)) {
          searchParams.delete('filter.v.price.lte');
        }
      }
      FacetFiltersForm.renderPage(searchParams.toString(), event);
    }

    onActiveFilterClick(href) {
      FacetFiltersForm.renderPage(href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1));
    }
  }

  FacetFiltersForm.filterData = [];
  FacetFiltersForm.controller = null;
  FacetFiltersForm.deferredRequest = null;
  FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
  FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
  customElements.define('facet-filters-form', FacetFiltersForm);
  FacetFiltersForm.setListeners();
}

if (!customElements.get('facet-remove')) {
  class FacetRemove extends HTMLElement {
    constructor() {
      super();
      // One listener for the links inside, also those rendered later; a facet-remove nested in another handles its own.
      this.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');
        if (!link || link.closest('facet-remove') !== this) return;
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(link.href);
      });
    }
  }
  customElements.define('facet-remove', FacetRemove);
}

/**
 *  @class
 *  @function PriceSlider
 */
if (!customElements.get('price-slider')) {
  class PriceSlider extends HTMLElement {
    // A typed price that is not what the slider holds yet (the slider moves only on a commit).
    hasUncommittedPrice() {
      const slider = this.querySelector('.price_slider');
      if (!slider || !slider.noUiSlider) return false;
      const held = slider.noUiSlider.get().map(parseFloat);
      return Array.from(this.querySelectorAll('.price_slider_amount .field__input')).some((input, i) => {
        const typed = parseFloat(input.value.replace(/[^\d.]/g, ''));
        return Number.isNaN(typed) || typed !== held[i];
      });
    }
    connectedCallback() {
      let slider = this.querySelector('.price_slider'),
        amounts = this.querySelector('.price_slider_amount'),
        args = {
          start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || slider.dataset.max)],
          connect: true,
          step: 10,
          direction: document.dir,
          handleAttributes: [
            { 'aria-label': slider.dataset.minLabel || 'Minimum price' },
            { 'aria-label': slider.dataset.maxLabel || 'Maximum price' },
          ],
          range: {
            'min': 0,
            'max': parseFloat(slider.dataset.max)
          }
        };
      // A price is committed from its own field, so the request knows its row and keeps it (the shopper may be typing
      // in the other field) rather than re-rendering it.
      const commit = (field) => field.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: { commit: true } }));

      // A live slider is rebuilt; markup copied from one (classes and handles without the instance) is cleared.
      if (slider.noUiSlider) {
        slider.noUiSlider.destroy();
      } else if (slider.classList.contains('noUi-target')) {
        slider.className = slider.className.split(' ').filter((name) => !name.startsWith('noUi-')).join(' ');
        slider.replaceChildren();
      }
      noUiSlider.create(slider, args);

      slider.noUiSlider.on('update', function (values) {
        amounts.querySelector('.field__input_min').value = values[0];
        amounts.querySelector('.field__input_max').value = values[1];
      });
      slider.noUiSlider.on('change', function () {
        commit(amounts.querySelector('.field__input_max'));
      });

      // A typed price applies once, when the shopper commits it (leaves the field or presses Enter), not on every
      // pause in typing; the handles move to it first, so the slider, the fields and the results agree.
      if (this.fieldsBound) return;
      this.fieldsBound = true;
      // Typing a price is not a choice yet, but it supersedes a request still loading (the other field's commit), so
      // that response cannot re-render the row under the shopper's fingers. The row then holds that change and
      // submits it when the shopper leaves the price fields, also when the typed value ends unchanged (no change event).
      amounts.addEventListener('input', (e) => {
        if (e.detail && e.detail.commit) return;
        e.stopPropagation();
        const Facets = customElements.get('facet-filters-form');
        if (Facets && Facets.controller) {
          Facets.cancelPending();
          Facets.setLoading(false);
          this.held = true;
        }
      });
      amounts.addEventListener('focusout', (e) => {
        if (!this.held || amounts.contains(e.relatedTarget)) return;
        this.held = false;
        commit(e.target.closest('.field__input') || amounts.querySelector('.field__input_max'));
      });
      amounts.addEventListener('change', (e) => {
        e.stopPropagation();
        const min = amounts.querySelector('.field__input_min');
        const max = amounts.querySelector('.field__input_max');
        // An empty or unreadable field leaves its handle where it is (noUiSlider skips null).
        const parse = (input) => {
          const value = parseFloat(input.value.replace(/[^\d.]/g, ''));
          return Number.isNaN(value) ? null : value;
        };
        slider.noUiSlider.set([parse(min), parse(max)]);
        this.held = false;
        commit(e.target.closest('.field__input') || max);
      });
      amounts.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' || !e.target.matches('.field__input')) return;
        e.preventDefault();
        e.target.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('price-slider', PriceSlider);
}

// Swatch tooltips (horizontal filters): bound once, even if the theme editor runs this script again.
if (!window.facetTooltipsBound) {
  window.facetTooltipsBound = true;
  const updateShift = (label) => {
    const content = label.querySelector('.facet-tooltip__content');
    if (!content) return;
    label.style.setProperty('--tooltip-shift', '0px');
    const rect = content.getBoundingClientRect();
    const gutter = 8;
    const vw = document.documentElement.clientWidth;
    let shift = 0;
    if (rect.left < gutter) {
      shift = Math.round(gutter - rect.left);
    } else if (rect.right > vw - gutter) {
      shift = Math.round(vw - gutter - rect.right);
    }
    label.style.setProperty('--tooltip-shift', shift + 'px');
  };

  document.addEventListener('mouseover', (e) => {
    if (!e.target.closest) return;
    const label = e.target.closest('.facet-checkbox--tooltip');
    if (!label || label._tooltipHovered) return;
    label._tooltipHovered = true;
    updateShift(label);
  });

  document.addEventListener('mouseout', (e) => {
    if (!e.target.closest) return;
    const label = e.target.closest('.facet-checkbox--tooltip');
    if (!label) return;
    if (!label.contains(e.relatedTarget)) {
      label._tooltipHovered = false;
    }
  });
}
